<!-- This component is used to display the payment step of the Signing Flow -->
<template>
	<section
		v-if="proposedInsured"
		class="mt-10 flex h-full flex-col gap-4 px-6 py-10"
	>
		<FormKit
			#default="{ state: { valid } }"
			v-model="proposedInsured.payment.paymentDetails!"
			:key="`paymentDetails-${proposedInsured.id}`"
			type="form"
			:actions="false"
			form-class="w-full grow h-full shrink-0 relative"
		>
			<Transition
				name="fade"
				mode="out-in"
			>
				<component
					:is="currentStepComponent"
					:paymentDetails="proposedInsured.payment.paymentDetails"
				/>
			</Transition>

			<Teleport
				defer
				to="#paymentNext"
				v-if="
					proposedInsured?.paymentStep === 'html' &&
					proposedInsured.payment.paymentType !== PlanPaymentType.CreditCard
				"
			>
				<button
					type="button"
					aria-label="paymentNextButton"
					class="text-accent-700! h-12 w-52 justify-center rounded-md border-0 font-bold transition-all duration-300"
					:class="
						valid
							? 'bg-accent-400! hover:bg-accent-500! hover:text-accent-800!'
							: 'bg-secondary-300! hover:bg-secondary-300! cursor-not-allowed text-slate-500/50! hover:text-slate-500/50!'
					"
					@click="valid && sessionDocumentsStore.setPaymentStepTo('document')"
				>
					{{ t('document.authorizePayment') }}
				</button>
			</Teleport>
		</FormKit>
	</section>
</template>

<script setup lang="ts">
import CreditCardForm from './CreditCardForm.vue'
import DirectBillForm from './DirectBillForm.vue'
import EFTForm from './EFTForm.vue'
import { PlanPaymentType } from '@/enums/PlanPaymentType'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { PaymentType } from '@precoa/calculator-sdk'
import { computed } from 'vue'

const { t } = usePresentationLocale()
const planStore = usePlanStore()
const sessionDocumentsStore = useSessionDocumentsStore()

// Used for v-modeling the payment properties of the proposed insured
const proposedInsured = computed({
	get: () => planStore.getPlan(sessionDocumentsStore.currentPlanHolderID),
	set: (value) => value && proposedInsured.value?.update(value),
})

// This function determines which component to load depending on the insurance payment type
const currentStepComponent = computed(() => {
	switch (proposedInsured.value?.insurance.paymentType) {
		case PaymentType.SP_EFT:
		case PaymentType.MP_EFT:
			return EFTForm
		case PaymentType.BILL:
			return DirectBillForm
		case PaymentType.CREDIT_CARD:
			return CreditCardForm
	}
})
</script>

<style scoped>
@reference "tailwindcss";
:deep(.formkit-label.required::after) {
	@apply ml-1.5 text-red-500 content-['*'];
}

.fade-enter-active,
.fade-leave-active {
	transition:
		opacity 0.2s ease,
		transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
	transform: translateY(20px);
	opacity: 0;
	position: absolute;
	top: 0;
}
</style>
